// TransactionImpact.jsx
import React, { useEffect, useState } from "react";
import { getFullName } from "../../../../Modules/utils/fullname";
import api from "../../../../Services/Api/api";
import { formatTypeLabel } from "./SelectTransactionType";
import { formatPhone } from "../../../../Modules/utils/etc";

const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const fmtDateShort = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "-";

const TrCard = ({ data }) => {
  const { title, titleMuted, value, cols } = data;

  return (
    <div className="card mb-2" style={{ fontSize: 12 }}>
      <div className="card-body p-2">
        <div className="d-flex justify-content-between mb-1">
          <div>
            <div className="d-flex align-items-baseline">
              <div className="me-2 fw-semibold" style={{ fontSize: 12 }}>
                {title}
              </div>
              <div className="text-muted small">{titleMuted}</div>
            </div>
          </div>

          <div className="fw-bold text-end" title="Total paid">
            {value}
          </div>
        </div>

        <div className="row gx-0 gy-0 mt-0">
          {cols.map(({ title, value }, i) => (
            <div className="col-3" key={i}>
              <div className="small text-muted">{title}</div>
              <div className="small">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function TransactionCardCompact({ t }) {
  // destructure commonly used metrics with safe fallbacks
  const totalPaid = t.totalAmount ?? 0;
  const totalCOD = t.cod?.totalCOD ?? 0;
  const totalRTO =
    t?.rtoLossList?.reduce((s, r) => s + Number(r.amount), 0) ?? 0;
  const deliveryCount = t.cod?.deliveryCount ?? 0;
  const pickupCount = t.cod?.pickupCount ?? 0;
  const deliveryRate = t.cod?.deliveryRate ?? 0;
  const pickupRate = t.cod?.pickupRate ?? 0;
  const deltaUser = t.userBalanceDue?.current ?? 0;
  const deltaCommission = t.commissionBalance?.current ?? 0;
  const runningUser = t.userBalanceDue?.total ?? "";
  const runningCommission = t.commissionBalance?.total ?? "";

  return (
    <TrCard
      data={{
        title: formatTypeLabel(t.transactionType),
        titleMuted: fmtDateShort(t.date),
        value: INR(totalPaid),
        cols: [
          {
            title:
              totalCOD && totalRTO
                ? "COD + RTO Loss"
                : totalRTO
                  ? "RTO Loss"
                  : "Total COD",
            value: INR(totalCOD + totalRTO),
          },
          {
            title: <>Delivery ({INR(deliveryRate)})</>,
            value: deliveryCount,
          },
          { title: <>Pickup ({INR(pickupRate)})</>, value: pickupCount },
          { title: "Commission", value: INR(deltaCommission) },
        ],
      }}
    />
  );
}

// ---------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------
export default function TransactionImpact({ data: transaction, opt = {} }) {
  const transactionId = transaction?._id;
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!transactionId) return;

    let alive = true;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await api.get(
          `v1/office/summary/transaction/${transactionId}/settlement-history`,
        );

        if (!alive) return;

        let computed = {
          totalCOD: 0,
          totalRTO: 0,
          totalPickups: 0,
          totalDelveries: 0,
          computedTotalComm: 0,
        };

        for (let i = 0; i < result.inBetweenSettlementTrs?.length; i++) {
          const t = result.inBetweenSettlementTrs?.[i];
          const totalRTOLoss =
            t?.rtoLossList?.reduce((s, r) => s + Number(r.amount), 0) ?? 0;

          computed = {
            totalRTO: computed.totalRTO + totalRTOLoss,
            totalCOD: computed.totalCOD + (t.cod?.totalCOD || 0),
            totalPickups: computed.totalPickups + (t.cod?.pickupCount || 0),
            totalDelveries: (computed.totalDelveries +=
              t.cod?.deliveryCount || 0),
            computedTotalComm: (computed.computedTotalComm +=
              t.commissionBalance?.current || 0),
          };
        }
        // actual API success
        setPayload({
          ...result,
          browserComputed: computed,
        });
        setLoading(false);
      } catch (err) {
        console.error("API failed", err);

        if (!alive) return;

        setLoading(false);
      }
    })();

    return () => (alive = false);
  }, [transactionId]);

  if (loading) {
    return (
      <div className="card p-3 text-center text-muted">
        Loading settlement history…
      </div>
    );
  }

  if (error) {
    return <div className="card p-3 text-danger">Error: {error}</div>;
  }

  if (!payload) {
    return <div className="card p-3 text-muted">No data available.</div>;
  }

  // extracted fields
  const agent = payload.agent || {};
  const prevSett = payload.prevToLastSettlement || null;
  const between = payload.inBetweenSettlementTrs || [];
  const lastSett = payload.lastSettlement || null;
  const currentSett = payload.currentSettlement || null;
  const since = payload.trsSinceLastSettlement || [];
  const browserComputed = payload.browserComputed || null;

  // ---------------------------------------------
  // RENDER HELPERS
  // ---------------------------------------------
  function getSettlementDateRange(prevSett, lastSett) {
    if (!prevSett && !lastSett) {
      return "No past records";
    }

    if (!prevSett && lastSett?.date) {
      return `To ${formatFullDate(lastSett.date)}`;
    }

    const prevDate = new Date(prevSett.date);
    const lastDate = new Date(lastSett.date);

    const sameYear = prevDate.getFullYear() === lastDate.getFullYear();

    if (sameYear) {
      return `From ${formatDayMonth(prevDate)} to ${formatDayMonth(
        lastDate,
      )} ${lastDate.getFullYear()}`;
    }

    return `From ${formatFullDate(prevDate)} to ${formatFullDate(lastDate)}`;
  }

  function formatFullDate(date) {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  function formatDayMonth(date) {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
  }

  const renderHeader = (sett = currentSett) => (
    <div className="mb-3">
      <table className="table table-sm">
        <tbody>
          <tr>
            <td>
              <div className="fw-bold">{getFullName(agent)}</div>
            </td>
            <td className="text-end small">
              <div className="fw-semibold">{formatPhone(agent)}</div>
            </td>
          </tr>
          <tr>
            <td>Date Range</td>
            <td className="text-end small">
              {getSettlementDateRange(prevSett, lastSett)}
            </td>
          </tr>
          <tr>
            <td>Balance Due Before Settlement</td>
            <td className="text-end">
              {INR(sett.userBalanceDue?.total - sett.userBalanceDue?.current)}
            </td>
          </tr>
          <tr>
            <td>Commission Earned</td>
            <td className="text-end fw-bold">{INR(sett.totalAmount)}</td>
          </tr>
          <tr>
            <td>Balance Due After Settlement</td>
            <td className="text-end">
              {INR(sett.userBalanceDue?.total)}
            </td>
          </tr>
          <tr>
            <td>Amount Payable by Office</td>
            <td className="text-end fw-bold">
              {INR(Math.max(0, -sett.userBalanceDue?.total))}
            </td>
          </tr>
          {/* <tr>
            <td>Commission Balance </td>
            <td className="text-end fw-bold">
              {INR(sett.commissionBalance?.total)}
            </td>
          </tr> */}
        </tbody>
      </table>
    </div>
  );

  const renderTotals = (data) => (
    <TrCard
      data={{
        title: <span className="fw-bold">Total</span>,
        titleMuted: "",
        value: "",
        cols: [
          {
            title: <>Total Delivery </>,
            value: <span className="fw-bold">{data.totalDelveries}</span>,
          },
          {
            title: <>Total Pickup</>,
            value: <span className="fw-bold">{data.totalPickups}</span>,
          },
          { title: "Commission", value: INR(data.computedTotalComm) },
          {
            title: "RTO Loss",
            value: INR(data.totalRTO),
          },
        ],
      }}
    />
  );

  const renderCompactCardList = (
    list = [],
    title = "Transactions",
    data = {},
  ) => (
    <div className="mb-3">
      <h6 className="mb-2" style={{ fontSize: 13 }}>
        {title}
      </h6>
      <div>
        {list.length === 0 && (
          <div className="text-muted small">No transactions</div>
        )}
        {list.map((t) => (
          <TransactionCardCompact key={t._id} t={t} />
        ))}
        {renderTotals(data)}
      </div>
    </div>
  );

  // ---------------------------------------------
  // MAIN VIEW
  // ---------------------------------------------
  return (
    <div className="">
      {renderHeader()}
      {opt?.transactionsMode === "totalOnly"
        ? renderTotals(browserComputed || {})
        : renderCompactCardList(between, "Transactions", browserComputed || {})}
    </div>
  );
}
